// **THE VIDEO COVER POPUP — MOVED HERE VERBATIM BY B1 (2026-08-15), AND THE MOVE IS THE CEILING
// BEING PAID BY THE ROUND THAT SPENT IT** (R8: App.tsx pays its own ceiling as it goes,
// opportunistically, in whatever area a round is already touching). B1 gave this component its
// `canStream` prop and its third face, which is what makes it this round's own surface rather than
// a stranger's file dragged in to make a number fit.
//
// **WHAT THE MOVE BUYS BEYOND THE LINES:** this popup is where the video gate LIVES now — inside
// App.tsx that decision could only ever be checked by a regex over eleven thousand lines. Out here
// it is importable, so a fixture can read the one thing that matters about it: that all three faces
// are dispatched from `videoState` and from nothing else.
//
// It renders `ModalBackdrop`, which holds two effects, so this is NOT one of the hookless leaves a
// gate CALLS — it is the stateful class `TierCards`, `MicButton` and `SignInModal` already moved as.
// The reach it buys is the import, not the render.
import { useState } from "react";
import { ModalBackdrop } from "./ModalBackdrop";
import { useEscapeKey } from "./useEscapeKey";
import { videoState } from "../videoAccess.js";
import { t } from "../strings.js";
import type { ShelfBook } from "../lib/shelves";
// ITEM G3 — in the store app a video she has not bought says so, with no price and no button (R3, P2).
import { APP_MODE } from "../lib/appMode";
import { NotIncluded } from "../storeApp/NotIncluded";

// Q6: a video's FULL, uncropped cover in a popup with a Play button. The shelf thumbnails may
// stay cropped-to-fit (they are small and dense); this popup is the readable moment — the whole
// cover, its title legible, then play starts the in-site player from here.
// T1-7 — SPIRIT KEEPING. One video in the Library is not part of any membership: it is bought once
// and is hers for good, whatever her tier does afterwards. So this modal has two faces, and which
// one it wears is the SERVER's answer (`purchases`), never a guess from the title.
// B1 — AND IT CARRIES ALL THREE OF `videoState`'s FACES NOW, BECAUSE THE GATE MOVED HERE. It used to
// be reachable only after `requireFeature("library")` had already passed, so it hard-coded
// `canStream: true` and could never draw the LOCKED face. A free explorer reaches it now, which is
// the whole point of her clause: she sees the cover, the title, the blurb and the PRICE, and meets
// the wall on the tap that would cost something. One state, computed once, dispatched three ways —
// `buy` is answered by the price and NEVER by an upgrade (W1-2), `locked` raises the purple box, and
// `play` includes a video she bought whatever her tier does since (T1-7).
export function VideoCoverModal({ book, onPlay, onLocked, onClose, product, owned, canStream, language }: {
  book: ShelfBook;
  language: string;
  onPlay: () => void;
  onLocked: () => void;
  onClose: () => void;
  // The one-time product this video belongs to, when it belongs to one.
  product?: { key: string; label: string; priceText: string; blurb: string } | null;
  owned?: boolean;
  canStream?: boolean;
}) {
  const [imgOk, setImgOk] = useState(true);
  const [buying, setBuying] = useState(false);
  const [buyNote, setBuyNote] = useState("");
  const state = videoState(product, Boolean(owned), Boolean(canStream));
  const mustBuy = state === "buy";
  // The tap the two non-buy faces share. A locked one is not a dead control — tapping it is HOW she
  // meets the box (FREE-1), so no new copy is needed for the third face.
  const tapPlay = state === "play" ? onPlay : onLocked;

  async function buy() {
    // F1 (her box, 2026-10-17): in the store app a paid video is no buy path (Apple 3.1.3: no purchase outside in-app
    // purchase); its popup says "Not included in your plan" instead, and the cover tap does nothing.
    if (APP_MODE) return;
    setBuying(true);
    setBuyNote("");
    try {
      const res = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // The return path brings her back to the Library, where the video now plays.
        body: JSON.stringify({ product: product?.key, returnTo: "/library" }),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.checkoutUrl) { window.location.href = data.checkoutUrl; return; }
      // 14-B3's seam: the SERVER's own refusal wins; only the fallback is ours to say.
      setBuyNote(data?.message || t(language, "video.checkoutFailed"));
    } catch {
      setBuyNote(t(language, "video.checkoutFailed"));
    }
    setBuying(false);
  }

  useEscapeKey(onClose);
  return (
    <ModalBackdrop label={t(language, "video.coverLabel", { title: book.title })} className="video-cover-backdrop">
      <div className="video-cover-modal" onMouseDown={(e) => e.stopPropagation()}>
        <button className="modal-close" type="button" onClick={onClose} aria-label={t(language, "common.close")}>x</button>
        <button
          type="button"
          className={state === "play" ? "video-cover-modal__art" : "video-cover-modal__art is-locked"}
          onClick={mustBuy ? buy : tapPlay}
          aria-label={t(language, state === "buy" ? "video.get" : state === "locked" ? "video.locked" : "video.play", { title: book.title })}
        >
          {imgOk ? (
            <img className="video-cover-modal__img" src={`/api/library/cover/${book.slug}`} alt={book.title} onError={() => setImgOk(false)} />
          ) : (
            <span className="video-cover-modal__placeholder">{book.title}</span>
          )}
          <span className="video-cover-modal__play-badge" aria-hidden="true">{state === "play" ? "▶" : "🔒"}</span>
        </button>
        <h2 className="video-cover-modal__title">{book.title}</h2>
        {mustBuy ? (
          <>
            <p className="video-cover-modal__blurb">{product?.blurb}</p>
            {APP_MODE ? <NotIncluded /> : (<>
            {/* Bought once, hers for good, and the box says so before she pays. */}
            <p className="video-cover-modal__keep">{t(language, "video.keep")}</p>
            <button type="button" className="video-cover-modal__buy" onClick={buy} disabled={buying}>
              {buying ? t(language, "video.openingCheckout") : t(language, "video.getFor", { price: product?.priceText })}
            </button>
            {buyNote ? <p className="video-cover-modal__note">{buyNote}</p> : null}
            </>)}
          </>
        ) : (
          <>
            {product ? <p className="video-cover-modal__owned">{t(language, "video.owned")}</p> : null}
            <button type="button" className="button button--primary video-cover-modal__play" onClick={tapPlay}>
              {t(language, "video.playVideo")}
            </button>
          </>
        )}
      </div>
    </ModalBackdrop>
  );
}
